import React, { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { PlusCircle as IconPlusCircle } from 'react-feather'
import classnames from 'classnames'
import _ from 'lodash'

import { Show } from './commons'
import styled from 'styled-components'
import { queries } from '~/constants/mediaQuery'

function useCombinedRefs(...refs) {
  const targetRef = React.useRef()

  React.useEffect(() => {
    refs.forEach(ref => {
      if (!ref) return

      if (typeof ref === 'function') {
        ref(targetRef.current)
      } else {
        ref.current = targetRef.current
      }
    })
  }, [refs])

  return targetRef
}

const DEFAULT_VARIABLES = [
  { label: 'Local name', value: '{{local_name}}' },
  { label: 'Local city', value: '{{local_city}}' },
  { label: 'Local area', value: '{{local_area}}' },
  { label: 'Local state', value: '{{local_state}}' },
  { label: 'Store code', value: '{{store_code}}' },
  { label: 'Store hours', value: '{{store_hours}}' },
  { label: 'First name', value: '{{first_name}}' },
]

function VariablesDropdown({ onChange }) {
  const [open, setOpen] = React.useState(false)
  return (
    <>
      <VariableButton onClick={() => setOpen(value => !value)}>
        <IconPlusCircle
          size={10}
          style={{ color: '#fff', marginRight: '5px' }}
        />
        <FormattedMessage id="Variables" />
        <Show condition={open}>
          <VariableList>
            {DEFAULT_VARIABLES.map((variable, i) => (
              <VariableItem key={i} onClick={() => onChange(variable)}>
                <FormattedMessage id={variable.label} />
              </VariableItem>
            ))}
          </VariableList>
        </Show>
      </VariableButton>
    </>
  )
}

function Textarea(
  {
    maxLength,
    onChange,
    setValue,
    className,
    invalid,
    onBlur,
    children,
    enableVariables = false,
    value,
    ...props
  },
  ref
) {
  const inputRef = useRef(null)
  const combinedRefs = useCombinedRefs(ref, inputRef)
  const [length, setLength] = useState(0)
  const [focus, setFocus] = useState(false)

  React.useEffect(() => {
    setLength(value?.length || 0)
  }, [value])

  return (
    <>
      <div className="form-label-group mb-0">
        <Show condition={enableVariables}>
          <VariablesDropdown
            onChange={variable => setValue(`${value}${variable.value}`)}
          />
        </Show>
        <InputEl
          ref={combinedRefs}
          className={`form-control ${className || ''} ${classnames({
            'border-danger': invalid,
          })}`}
          as="textarea"
          rows="3"
          onChange={e => {
            setLength(e.target.value.length)
            if (onChange) onChange(e)
          }}
          onFocus={() => setFocus(true)}
          onBlur={e => {
            setFocus(false)
            if (_.isFunction(onBlur)) {
              onBlur(e)
            }
          }}
          value={value}
          {...props}
        />
        {children && (
          <div className="position-absolute" style={{ right: 0, top: 0 }}>
            {children}
          </div>
        )}
      </div>
      <Show condition={!!maxLength}>
        <small
          className={`counter-value float-right
            ${classnames({
              'bg-secondary': props?.disabled || !focus,
              'bg-danger': length > maxLength,
            })}
          `}
        >
          {`${length || '0'}/${maxLength}`}
        </small>
      </Show>
    </>
  )
}

const InputEl = styled.textarea`
  resize: ${props => props.resize ?? 'none'};
`

const VariableButton = styled.div`
  background-color: #0091ff;
  height: 16px;
  font-size: 10px;
  color: #fff;
  border-radius: 8px 8px 0px 0px;
  line-height: 10px;
  font-family: 'Montserrat';
  margin-top: -16px;
  right: 0;
  display: flex;
  align-items: center;
  padding: 0 12px;
  margin-right: 10px;
  cursor: pointer;
  position: absolute;
  overflow: visible;

  @media ${queries.less.sm} {
    display: none;
  }
`

const VariableList = styled.ul`
  position: absolute;
  width: 100%;
  left: 0;
  top: 18px;
  z-index: 100;
  padding: 0;
  box-shadow: 0px 4px 8px 2px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background-color: #fff;
`

const VariableItem = styled.li`
  z-index: 1000;
  list-style: none;
  padding: 2px 5px;
  color: #565656;
  user-select: none;

  &:hover {
    background: rgba(0, 145, 255, 0.7);
    color: #fff;
  }
`

export default React.forwardRef(Textarea)
